import React from "react";
import { Box, Typography, Divider } from "@mui/material";
import Layout from "../../layouts/Layout";

const TermsOfUse = () => {
  return (
    <Layout>
      <Box
        sx={{
          px: { xs: 2, md: 8 },
          py: { xs: 4, md: 8 },
          maxWidth: "1200px",
          mx: "auto",
        }}
      >
        {/* Header Section */}
        <Box sx={{ textAlign: "center", mb: 6 }}>
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 2 }}>
            Handpik.ai — Terms of Use
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "text.secondary", maxWidth: "700px", mx: "auto" }}
          >
            Welcome to Handpik.ai. By accessing or using our websites, apps, and
            related services (collectively, the “Services”), you agree to these
            Terms of Use (“Terms”) and our Privacy Policy.
          </Typography>
        </Box>

        <Divider sx={{ my: 5 }} />

        {/* 1. Who can use Handpik.ai */}
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
          1) Who Can Use Handpik.ai
        </Typography>
        <Typography variant="body1" sx={{ color: "text.secondary", mb: 4 }}>
          You must be at least 13 years old (or the age required by local law)
          to use the Services. If you create an account, you are responsible for
          keeping your credentials confidential and for all activity under your
          account.
        </Typography>

        <Divider sx={{ my: 5 }} />

        {/* 2. License and acceptable use */}
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
          2) License and Acceptable Use
        </Typography>
        <Typography variant="body1" sx={{ color: "text.secondary", mb: 2 }}>
          We grant you a limited, personal, non-exclusive, non-transferable
          license to access and use the Services according to these Terms. You
          agree you will not:
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 4 }}>
          {[
            "Reverse engineer, decompile, or attempt to extract source code, except as permitted by law.",
            "Use any scraping, crawling, or automated means without our prior written consent.",
            "Upload or transmit unlawful, defamatory, deceptive, infringing, or harmful content.",
            "Interfere with or disrupt the Services or circumvent access controls.",
            "Misrepresent reviews, ratings, endorsements, or pricing.",
          ].map((item, i) => (
            <Typography
              key={i}
              component="li"
              sx={{ mb: 1, color: "text.secondary" }}
            >
              {item}
            </Typography>
          ))}
        </Box>

        <Divider sx={{ my: 5 }} />

        {/* 3. AI-generated results */}
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
          3) AI-Generated Results and Shopping Guidance
        </Typography>
        <Typography variant="body1" sx={{ color: "text.secondary", mb: 4 }}>
          Handpik uses AI to summarize information and recommend products.
          Outputs are informational only and may not always be accurate or
          available in your region. Always verify critical details (e.g., price,
          availability, size, warranty, delivery/return policies) directly with
          the retailer before purchasing.
        </Typography>

        <Divider sx={{ my: 5 }} />

        {/* 4. Third-party retailers */}
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
          4) Third-Party Retailers and Marketplaces
        </Typography>
        <Typography variant="body1" sx={{ color: "text.secondary", mb: 4 }}>
          We link to and work with third-party retailers, brands, affiliate
          networks, and marketplaces (collectively, “Retailers”). Purchases are
          made with Retailers, not Handpik. Retailers are solely responsible for
          order acceptance, fulfillment, shipping, returns/exchanges, product
          quality, warranties, and customer support. Handpik does not
          control—and is not responsible for—Retailer websites, policies, or
          practices.
        </Typography>

        <Divider sx={{ my: 5 }} />

        {/* 5. Intellectual property */}
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
          5) Intellectual Property
        </Typography>
        <Typography variant="body1" sx={{ color: "text.secondary", mb: 4 }}>
          All rights in the Services and their content—including software, text,
          graphics, logos, images, layouts, and trademarks (collectively,
          “Content”)—are owned by Handpik or our licensors and are protected by
          applicable laws. Except as expressly allowed in these Terms, you may
          not copy, modify, adapt, publish, distribute, sell, or create
          derivative works from the Content without prior written permission
          from the rights holder.
          <br />
          <br />
          If you submit or upload content (e.g., reviews, comments, prompts,
          images), you grant Handpik a worldwide, non-exclusive, royalty-free
          license to host, store, reproduce, adapt, publish, and display such
          content to operate, improve, and promote the Services. You represent
          that you have the necessary rights to your submissions.
        </Typography>

        <Divider sx={{ my: 5 }} />

        {/* 6. Cashback & rewards */}
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
          6) Cashback & Rewards (if offered)
        </Typography>

        <Typography variant="body1" sx={{ color: "text.secondary", mb: 2 }}>
          Handpik may operate a cashback and/or rewards feature (collectively,
          the “Cashback Program”). Details vary by Retailer and are subject to
          change.
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 1, mb: 1 }}>
          6.1 Qualifying Transactions
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
          {[
            "you click from Handpik to a participating Retailer,",
            "complete a valid purchase in the same session (without ad blockers or tracking blockers),",
            "the order is delivered and the return/refund period has passed, and",
            "the Retailer or its tracking partner confirms and pays the corresponding commission to Handpik.",
          ].map((item, i) => (
            <Typography
              key={i}
              component="li"
              sx={{ mb: 1, color: "text.secondary" }}
            >
              {item}
            </Typography>
          ))}
        </Box>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          Only then will the transaction be marked validated; before that it
          appears as pending. Some Retailers do not participate in cashback;
          some transactions (e.g., coupon use, gift cards, subscription
          renewals, cancellations, returns, or exchanges) may be excluded at the
          Retailer’s discretion.
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
          6.2 Tracking & Attribution
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          If a Retailer does not track a transaction to Handpik via its
          affiliate system, or tracking is broken/overwritten by another source
          (e.g., another cashback site/app, browser extension, or cookie
          restrictions), we may not receive commission and therefore cannot pay
          cashback. We are not liable for untracked or declined transactions.
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
          6.3 Rates, Timing & Adjustments
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          Cashback rates shown on the Services are indicative and can change at
          any time. Your final cashback is the amount actually reported and paid
          to Handpik by the Retailer/affiliate network, which may be higher or
          lower than advertised. Validation can take 90 days or more from
          purchase, depending on the Retailer. If an order is returned,
          exchanged, or refunded, the related cashback will be cancelled or
          reversed. We may adjust balances to correct errors, fraud, or
          non-payment.
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
          6.4 Missing Cashback Enquiries
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          If you believe a qualifying purchase is missing from your account, you
          must submit a ticket within 10 days of the transaction with order
          details we request. We will attempt to investigate with the
          Retailer/affiliate network, but we cannot guarantee recovery. If no
          commission is received, cashback cannot be paid. If a Retailer has not
          resolved a claim after six months, we may close the enquiry.
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
          6.5 Payouts & Minimums
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          You may withdraw validated cashback once your account reaches the
          minimum payout threshold shown in the Services, using the available
          payout methods (e.g., bank transfer, wallet, gift card), provided your
          payout details are accurate. Handpik is not responsible for payouts
          sent to incorrect details you supplied.
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
          6.6 Inactivity & Expiry
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          If you do not log in to your Handpik account for 6 months, pending
          and/or validated cashback may expire and be removed from your balance.
          Expired cashback cannot be reinstated or withdrawn.
        </Typography>

        <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
          6.7 Fraud & Abuse
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 4 }}>
          We may withhold, reverse, or reclaim cashback in cases of suspected
          fraud, misuse, gaming of the system, or where commission is not
          actually received from the Retailer or its agent. We may suspend or
          terminate accounts involved in abusive activity.
        </Typography>

        <Divider sx={{ my: 5 }} />

        {/* 7. Referral program */}
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
          7) Referral Program (if offered)
        </Typography>
        <Typography variant="body1" sx={{ color: "text.secondary", mb: 4 }}>
          From time to time, we may offer referral rewards. A Qualifying
          Referral and any reward amount or timing will be defined in the
          Services and may require both referrer and referee to earn validated
          cashback above a specified threshold. We may change, pause, or cancel
          the referral program at any time.
        </Typography>

        <Divider sx={{ my: 5 }} />

        {/* 8. Availability and maintenance */}
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
          8) Availability and Maintenance
        </Typography>
        <Typography variant="body1" sx={{ color: "text.secondary", mb: 4 }}>
          We aim to keep the Services available, but outages, maintenance
          windows, or internet/network issues may occur. Handpik is not
          responsible for any unavailability, delays, or data loss resulting
          from such events. Downloading or using any materials from the Services
          is at your own risk.
        </Typography>

        <Divider sx={{ my: 5 }} />

        {/* 9. Disclaimers */}
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
          9) Disclaimers
        </Typography>
        <Typography variant="body1" sx={{ color: "text.secondary", mb: 4 }}>
          THE SERVICES AND ALL CONTENT ARE PROVIDED “AS IS” AND “AS AVAILABLE”
          WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, OR
          STATUTORY, INCLUDING BUT NOT LIMITED TO WARRANTIES OF ACCURACY,
          MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT,
          OR AVAILABILITY. HANDPIK DOES NOT WARRANT PRICES, AVAILABILITY,
          DELIVERY TIMES, RETURNS/EXCHANGES, PRODUCT QUALITY, OR WARRANTY
          COVERAGE FROM ANY RETAILER.
        </Typography>

        <Divider sx={{ my: 5 }} />

        {/* 10. Limitation of liability */}
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
          10) Limitation of Liability
        </Typography>
        <Typography variant="body1" sx={{ color: "text.secondary", mb: 4 }}>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, HANDPIK AND ITS AFFILIATES,
          DIRECTORS, OFFICERS, EMPLOYEES, PARTNERS, AND LICENSORS WILL NOT BE
          LIABLE FOR: (A) INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL,
          EXEMPLARY, OR PUNITIVE DAMAGES; (B) LOST PROFITS, REVENUE, BUSINESS,
          GOODWILL, OR DATA; (C) ANY ISSUES RELATING TO ORDERS, DELIVERY,
          RETURNS/REFUNDS, OR PRODUCT QUALITY/WARRANTIES FROM RETAILERS; OR (D)
          ANY DAMAGES RESULTING FROM YOUR USE OF (OR INABILITY TO USE) THE
          SERVICES, UNAUTHORIZED ACCESS, OR THIRD-PARTY CONDUCT. OUR TOTAL
          LIABILITY FOR ANY CLAIMS RELATING TO THE SERVICES WILL NOT EXCEED THE
          GREATER OF (I) THE AMOUNT YOU PAID TO HANDPIK (IF ANY) IN THE 12
          MONTHS BEFORE THE CLAIM AROSE, OR (II) USD $50 (OR EQUIVALENT). Some
          jurisdictions do not allow certain disclaimers or limitations; in such
          cases, the above will apply to the fullest extent permitted by law.
        </Typography>

        <Divider sx={{ my: 5 }} />

        {/* 11. Indemnity */}
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
          11) Indemnity
        </Typography>
        <Typography variant="body1" sx={{ color: "text.secondary", mb: 4 }}>
          You agree to indemnify and hold harmless Handpik and its affiliates,
          officers, directors, employees, and agents from and against any
          claims, liabilities, damages, losses, and expenses (including
          reasonable legal fees) arising out of or in any way connected with
          your use of the Services, your content, or your breach of these Terms
          or applicable laws.
        </Typography>

        <Divider sx={{ my: 5 }} />

        {/* 12. Suspension and termination */}
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
          12) Suspension and Termination
        </Typography>
        <Typography variant="body1" sx={{ color: "text.secondary", mb: 4 }}>
          We may suspend or terminate your access to the Services at any time,
          with or without notice, for conduct that we believe violates these
          Terms, involves fraud or misuse, or harms other users, Handpik, or
          third parties. You may stop using the Services at any time. Certain
          sections (e.g., IP, disclaimers, liability limits, indemnity) survive
          termination.
        </Typography>

        <Divider sx={{ my: 5 }} />

        {/* 13. Privacy */}
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
          13) Privacy
        </Typography>
        <Typography variant="body1" sx={{ color: "text.secondary", mb: 4 }}>
          Our collection and use of personal information are described in our
          Privacy Policy. By using the Services, you consent to those practices.
        </Typography>

        <Divider sx={{ my: 5 }} />

        {/* 14. Governing law */}
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
          14) Governing Law & Dispute Resolution
        </Typography>
        <Typography variant="body1" sx={{ color: "text.secondary", mb: 4 }}>
          These Terms are governed by the laws of [Insert governing
          jurisdiction], without regard to its conflict of law rules. [Optional]
          Any disputes will be subject to the exclusive jurisdiction of the
          courts in [Insert venue]. If you prefer arbitration or a specific
          venue, replace this clause to reflect your corporate setup and target
          markets.
        </Typography>

        <Divider sx={{ my: 5 }} />

        {/* 15. Contact */}
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
          15) Contact
        </Typography>
        <Typography variant="body1" sx={{ color: "text.secondary", mb: 4 }}>
          Handpik AI, Inc.
          <br />
          Support and privacy inquiries:{" "}
          <Typography
            component="a"
            href="mailto:info@handpik.ai"
            sx={{
              color: "#FF7A00",
              textDecoration: "none",
              fontWeight: 500,
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            info@handpik.ai
          </Typography>
        </Typography>
      </Box>
    </Layout>
  );
};

export default TermsOfUse;
